import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Paperclip, Trash2, FileText, Image as ImageIcon, Upload } from "lucide-react";
import { toast } from "sonner";
import { confirmDialog } from "@/components/ConfirmDialog";

function iconFor(ct) {
  if ((ct || "").startsWith("image/")) return ImageIcon;
  return FileText;
}

function formatSize(n) {
  if (!n) return "—";
  if (n > 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  if (n > 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${n} B`;
}

/**
 * FileAttachments - inline component for showing & uploading files attached to a deal or contact.
 * Pass exactly one of dealId or contactId.
 */
export default function FileAttachments({ dealId, contactId }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const params = dealId ? { deal_id: dealId } : { contact_id: contactId };

  const load = async () => {
    const { data } = await api.get("/files", { params });
    setFiles(data);
  };

  useEffect(() => {
    if (dealId || contactId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId, contactId]);

  const onPick = () => inputRef.current?.click();

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      toast.error("File exceeds 20 MB limit");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      await api.post("/files/upload", fd, {
        params,
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Uploaded ${file.name}`);
      e.target.value = "";
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onDelete = async (id) => {
    if (!(await confirmDialog({ title: "Remove this file?", description: "It will be deleted from object storage and cannot be recovered.", confirmLabel: "Remove", destructive: true }))) return;
    await api.delete(`/files/${id}`);
    await load();
  };

  const onDownload = async (f) => {
    try {
      const res = await api.get(`/files/${f.id}/download`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: f.content_type });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = f.original_filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };

  return (
    <div className="mt-4" data-testid="file-attachments">
      <div className="flex items-center justify-between mb-3">
        <div className="overline text-navy/60 flex items-center gap-2"><Paperclip className="h-3.5 w-3.5" /> Attachments</div>
        <button
          type="button"
          onClick={onPick}
          disabled={uploading}
          data-testid="file-upload-btn"
          className="text-xs uppercase tracking-widest text-gold-deep hover:text-navy flex items-center gap-1.5 disabled:opacity-50"
        >
          <Upload className="h-3.5 w-3.5" /> {uploading ? "Uploading…" : "Upload"}
        </button>
        <input
          ref={inputRef}
          type="file"
          onChange={onUpload}
          className="hidden"
          data-testid="file-input"
          accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg,.gif,.webp,.txt,.csv"
        />
      </div>
      {files.length === 0 ? (
        <div className="text-xs italic text-navy/40 border border-dashed border-navy/15 py-4 text-center">
          No files attached
        </div>
      ) : (
        <ul className="space-y-2">
          {files.map((f) => {
            const Icon = iconFor(f.content_type);
            return (
              <li key={f.id} className="flex items-center gap-3 border border-navy/10 px-3 py-2" data-testid={`file-${f.id}`}>
                <Icon className="h-4 w-4 text-gold-deep flex-shrink-0" />
                <button onClick={() => onDownload(f)} className="flex-1 min-w-0 text-left text-sm text-navy hover:text-gold-deep truncate">
                  {f.original_filename}
                </button>
                <span className="text-xs text-navy/40">{formatSize(f.size)}</span>
                <button onClick={() => onDelete(f.id)} data-testid={`file-delete-${f.id}`} className="text-navy/40 hover:text-destructive">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
